import { map, findIndex, compact } from 'lodash';
import { connect } from 'react-redux';

import MasterProductCard from 'components/CustomCollectionProductCard/MasterProductCard';
import Product from 'models/Product';
import * as actions from 'state/actions';
import { ADD_TO_CART_ANALYTICS_ORIGINS } from 'state/constants';
import {
  getMasterProductData,
  getProductData,
  getMasterProductCarouselIndex,
  getCustomCollectionProductsData,
} from 'state/selectors/collection';
import CollectionPage from 'state/selectors/collection-page';
import Rudderstack from 'utils/Rudderstack';
import Settings from 'utils/Settings';

const DEFAULT_N_COLORS_PER_SLIDE = 6;

const mapStateToProps = (
  state,
  { masterHandle, nItemsPerSlide = DEFAULT_N_COLORS_PER_SLIDE, appearance, hasMobileQuickAddRow }
) => {
  const masterProduct = getMasterProductData(state, masterHandle);

  if (!masterProduct) {
    throw new Error(`Could not find master product with handle "${masterHandle}"`);
  }

  const currentColorway = new Product(getProductData(state, masterProduct.currentProduct));
  const colorways = map(masterProduct.products, productHandle => {
    const colorway = new Product(getProductData(state, productHandle));

    return {
      colors: colorway.colors,
      swatchImageUrl: colorway.swatchImageUrl,
      colorName: colorway.colorName,
      handle: colorway.handle,
      collectionProductCardImage: colorway.collectionProductCardImage,
      sortedSizes: colorway.sortedSizes,
      type: colorway.type,
    };
  });

  const currentSlideIndex = getMasterProductCarouselIndex(state, masterHandle);

  const cacheKey = `${masterHandle}--${colorways.length}--${nItemsPerSlide}`;

  const colorwayPickerSlides = CollectionPage.buildColorwayPickerSlides({
    colorways,
    nItemsPerSlide,
    cacheKey,
  });

  const { enableDiscountPriceColor } = Settings;

  const trackNavigation = ({ handle, url, event }) => {
    event?.preventDefault();
    event?.stopPropagation();

    const clickedColorway = getProductData(state, handle);
    const customCollectionProductsData = compact(getCustomCollectionProductsData(state));
    const position = findIndex(customCollectionProductsData, { master: clickedColorway.master });

    Rudderstack.navigationDelayForIphoneAndSafari({
      analyticsFn: () => {
        Rudderstack.productClicked({
          collection: CollectionPage.getCustomCollectionMetadata(
            state,
            customCollectionProductsData.length
          ),
          collectionItem: clickedColorway,
          position: position,
        });
      },
      urlToNavigate: url,
    });
  };

  return {
    appearance,
    currentColorway: {
      url: currentColorway.url,
      handle: currentColorway.handle,
      masterName: currentColorway.masterName,
      colorName: currentColorway.colorName,
      price: currentColorway.formattedPrice,
      compareAtPrice: currentColorway.formattedCompareAtPrice,
      image: currentColorway.collectionProductCardImage,
      colors: currentColorway.colors,
      swatchImageUrl: currentColorway.swatchImageUrl,
      sortedSizes: currentColorway.sortedSizes,
      type: currentColorway.type,
      badge: currentColorway.badge || '',
    },
    colorwayPickerSlides,
    masterHandle,
    currentSlideIndex,
    enableDiscountPriceColor,
    hasMobileQuickAddRow,
    trackNavigation,
  };
};

const mapDispatchToProps = dispatch => ({
  setMasterProductCardProduct: ({ masterHandle, productHandle }) => {
    dispatch(actions.setMasterProductCardProduct({ masterHandle, productHandle }));
  },
  onSizeSelection: (variantId, targetSelector) => {
    dispatch(
      actions.addLineItems(
        { [variantId]: 1 },
        { targetSelector, origination: ADD_TO_CART_ANALYTICS_ORIGINS.QUICK_ADD }
      )
    );
  },
  setMasterProductCardCarouselIndex: ({ masterHandle, carouselIndex }) => {
    dispatch(actions.setMasterProductCardCarouselIndex({ masterHandle, carouselIndex }));
  },
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onSwatchSelect: productHandle =>
    dispatchProps.setMasterProductCardProduct({
      masterHandle: stateProps.masterHandle,
      productHandle,
    }),
  onSlideIndexIncrement: () =>
    dispatchProps.setMasterProductCardCarouselIndex({
      masterHandle: stateProps.masterHandle,
      carouselIndex: stateProps.currentSlideIndex + 1,
    }),
  onSlideIndexDecrement: () =>
    dispatchProps.setMasterProductCardCarouselIndex({
      masterHandle: stateProps.masterHandle,
      carouselIndex: stateProps.currentSlideIndex - 1,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(MasterProductCard);
